// Chemin: C:\gestion-copro\src\pages\vigie\MaComptabilitePage.tsx
// Session 265 : Création — module "Ma comptabilité" fusionné
//   - Lit directement la GED (categorie: 'comptabilite') — source de vérité unique
//   - Pas de sous-collection appelsFonds : SENTINELLE archive dans la GED, la page lit la GED
//   - Zéro navigation entre modules : zone de dépôt intégrée → AjouterDocumentVigiePage
//     avec state contextuel { categorieDefaut: 'comptabilite', retourVers: '/vigie/comptabilite' }
//   - Profil adaptatif : occupant → charges copro · bailleur → charges + onglet Ma location
//   - donneesCles extraites par SENTINELLE affichées comme cockpit financier
// Remplace : VerificationComptableVigiePage + SuiviBailleurVigiePage

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { getDocuments, updateDocument } from '../../services/document.service';
import {
  getLocataires,
  createLocataire,
  archiverLocataire,
} from '../../services/suiviBailleur.service';
import { Document } from '../../types/document.types';
import {
  Locataire,
  CreateLocataireData,
} from '../../types/suiviBailleur.types';
import { TYPES_DOCUMENT_GED } from '../../constants/documentTaxonomy';
import {
  Calculator,
  Home,
  Building2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Upload,
  TrendingUp,
  TrendingDown,
  Wallet,
  Receipt,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';

// ============================================
// UTILITAIRES
// ============================================

const fmt = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

const fmtDate = (ts: { toDate: () => Date } | undefined) => {
  if (!ts) return '';
  return ts.toDate().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Types comptables à grouper comme "Appels de fonds & charges"
const TYPES_CHARGES_COPRO = ['appel_fonds', 'annexe', 'budget', 'releve', 'facture', 'devis'];
const TYPES_REGULARISATION = ['regularisation_charges'];

const TYPE_COLORS: Record<string, string> = {
  appel_fonds: 'bg-blue-100 text-blue-800',
  annexe: 'bg-indigo-100 text-indigo-800',
  budget: 'bg-purple-100 text-purple-800',
  releve: 'bg-cyan-100 text-cyan-800',
  facture: 'bg-orange-100 text-orange-800',
  devis: 'bg-yellow-100 text-yellow-800',
  regularisation_charges: 'bg-emerald-100 text-emerald-800',
};

// Extrait le montant depuis donneesCles — priorité aux clés connues, fallback strict, fallback résumé
// Exclut les clés qui contiennent des codes/références ou des données non-montant
const CLES_PARASITES = [
  'nombreLots', 'lot', 'bic', 'iban', 'reference', 'identifiant', 'code', 'siret',
  'tantiemes', 'adresse', 'description', 'nom', 'prenom', 'email', 'telephone',
  'etage', 'batiment', 'residence', 'copropriete', 'comptable', 'extranet',
];
const estCleParasite = (cle: string) =>
  CLES_PARASITES.some(p => cle.toLowerCase().includes(p));

// Vérifie qu'une valeur est purement numérique (ex: "342,83", "30.86", "-30,86") — rejette "155, RUE DES..."
// Accepte le signe moins (solde créditeur négatif dans donneesCles)
const estValeurPurementNumerique = (val: string): boolean =>
  /^-?\d{1,6}([.,]\d{1,2})?$/.test(val.trim());

const TYPES_REGULARISATION_SET = new Set(['regularisation_charges']);

const normaliserTexteResume = (s: string): string =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const extraireMontantDepuisResume = (resume: string): number | null => {
  // Normalisation NFD obligatoire — les accents (créditeur → crediteur) font rater les regex /i
  const rn = normaliserTexteResume(resume);
  // Stratégie 1 : pattern ciblé sur texte normalisé
  const patterns = [
    /solde\s+(?:de\s+la\s+regularisation|crediteur|debiteur|final|net)\s+(?:de\s+)?-?(\d{1,6}[.,]\d{1,2})[\s€]/i,
    /solde\s+de\s+-?(\d{1,6}[.,]\d{1,2})[\s€]/i,
    /montant\s+(?:du\s+)?solde\s*:?\s*-?(\d{1,6}[.,]\d{1,2})[\s€]/i,
    /difference\s*:?\s*-?(\d{1,6}[.,]\d{1,2})[\s€]/i,
    /regularisation\s*:?\s*-?(\d{1,6}[.,]\d{1,2})[\s€]/i,
  ];
  for (const pattern of patterns) {
    const match = rn.match(pattern);
    if (match) {
      const n = parseFloat(match[1].replace(',', '.'));
      if (!isNaN(n) && n > 0 && n < 100000) return n;
    }
  }
  return null;
};

const extraireMontant = (document: Document): number | null => {
  const estRegul = TYPES_REGULARISATION_SET.has(document.type);

  // Pour les régularisations : resume ou description comme source de vérité
  // resume = champ SENTINELLE standard ; description = champ utilisé par le flux direct (mode A)
  const texteResume = document.resume || (document as any).description || '';
  if (estRegul && texteResume) {
    const montantResume = extraireMontantDepuisResume(texteResume);
    if (montantResume !== null) return montantResume;
  }

  // Pour les appels de fonds : clés prioritaires dans donneesCles
  const cles = document.donneesCles;
  if (cles) {
    const clesPrioritaires = [
      'montant', 'montantAppele', 'montantTotal', 'totalGeneral', 'montantTTC',
      'montantDu', 'solde', 'total',
    ];
    for (const cle of clesPrioritaires) {
      const val = cles[cle];
      if (val !== undefined && val !== null && val !== '') {
        const str = String(val).trim();
        if (estValeurPurementNumerique(str)) {
          const n = Math.abs(parseFloat(str.replace(',', '.')));
          if (!isNaN(n) && n > 0 && n < 100000) return n;
        }
      }
    }
    // Fallback strict : valeur purement numérique sur clé non parasite
    for (const [cle, val] of Object.entries(cles)) {
      if (estCleParasite(cle)) continue;
      const str = String(val).trim();
      if (estValeurPurementNumerique(str)) {
        const n = Math.abs(parseFloat(str.replace(',', '.')));
        if (!isNaN(n) && n >= 1 && n < 100000) return n;
      }
    }
  }

  return null;
};

// Convertit "T1 2026" → "01/01/2026 au 31/03/2026", etc.
const convertirTrimestreEnDates = (val: string): string | null => {
  const match = val.trim().match(/^T([1-4])\s+(\d{4})$/i);
  if (!match) return null;
  const t = parseInt(match[1]);
  const y = match[2];
  const plages: Record<number, [string, string]> = {
    1: [`01/01/${y}`, `31/03/${y}`],
    2: [`01/04/${y}`, `30/06/${y}`],
    3: [`01/07/${y}`, `30/09/${y}`],
    4: [`01/10/${y}`, `31/12/${y}`],
  };
  const [debut, fin] = plages[t];
  return `${debut} au ${fin}`;
};

const extrairePeriode = (document: Document): string | null => {
  const cles = document.donneesCles;
  if (!cles) return null;
  if (cles['periode']) return cles['periode'];
  if (cles['trimestre']) {
    const converti = convertirTrimestreEnDates(String(cles['trimestre']));
    if (converti) return converti;
    return String(cles['trimestre']);
  }
  if (cles['exercice']) return String(cles['exercice']);
  return null;
};

const extraireStatut = (document: Document): string =>
  document.donneesCles?.['statutPaiement'] || 'en_attente';

const estDocumentRegle = (document: Document): boolean => {
  const s = extraireStatut(document);
  return s === 'regle' || s === 'oui' || s === 'paye' || s === 'payé';
};

// ============================================
// SOUS-COMPOSANT : Bandeau situation
// ============================================

const BandeauSituation: React.FC<{
  statut: 'ok' | 'due' | 'alert';
  label: string;
  detail?: string;
}> = ({ statut, label, detail }) => {
  const configs = {
    ok: { bg: 'bg-emerald-50 border-emerald-200', icon: <CheckCircle className="w-5 h-5 text-emerald-600" />, text: 'text-emerald-800' },
    due: { bg: 'bg-amber-50 border-amber-200', icon: <AlertTriangle className="w-5 h-5 text-amber-600" />, text: 'text-amber-800' },
    alert: { bg: 'bg-red-50 border-red-200', icon: <XCircle className="w-5 h-5 text-red-600" />, text: 'text-red-800' },
  };
  const c = configs[statut];
  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border ${c.bg}`}>
      {c.icon}
      <div>
        <p className={`font-semibold text-sm ${c.text}`}>{label}</p>
        {detail && <p className={`text-xs mt-0.5 ${c.text} opacity-80`}>{detail}</p>}
      </div>
    </div>
  );
};

// ============================================
// SOUS-COMPOSANT : Carte document comptable
// ============================================

const CarteDocument: React.FC<{
  document: Document;
  onMarquerRegle: (doc: Document) => void;
  onOuvrir: (doc: Document) => void;
  onSaisirMontant: (doc: Document, montant: number) => void;
  marquageEnCours: boolean;
}> = ({ document, onMarquerRegle, onOuvrir, onSaisirMontant, marquageEnCours }) => {
  const estRegularisation = TYPES_REGULARISATION.includes(document.type);
  const montant = extraireMontant(document);
  const periode = extrairePeriode(document);
  const regle = estDocumentRegle(document);
  const typeLabel = TYPES_DOCUMENT_GED[document.type as keyof typeof TYPES_DOCUMENT_GED] || document.type;
  const typeCouleur = TYPE_COLORS[document.type] || 'bg-gray-100 text-gray-700';

  // Saisie manuelle du montant
  const [modeEdition, setModeEdition] = React.useState(false);
  const [valeurSaisie, setValeurSaisie] = React.useState('');
  const [sauvegardeEnCours, setSauvegardeEnCours] = React.useState(false);

  // Modale règlement
  const today = new Date().toISOString().split('T')[0];
  const [showModalRegle, setShowModalRegle] = React.useState(false);
  const [moyenPaiement, setMoyenPaiement] = React.useState('virement');
  const [dateReglement, setDateReglement] = React.useState(today);

  const MOYENS_PAIEMENT = estRegularisation
    ? [
        { value: 'compensation', label: 'Déduit sur prochain appel' },
        { value: 'virement', label: 'Virement reçu' },
        { value: 'cheque', label: 'Chèque reçu' },
      ]
    : [
        { value: 'virement', label: 'Virement' },
        { value: 'prelevement', label: 'Prélèvement automatique' },
        { value: 'cheque', label: 'Chèque' },
        { value: 'especes', label: 'Espèces' },
      ];

  const handleConfirmerReglement = () => {
    onMarquerRegle({ ...document, _moyenPaiement: moyenPaiement, _dateReglement: dateReglement } as any);
    setShowModalRegle(false);
  };

  const handleConfirmerMontant = async () => {
    const n = parseFloat(valeurSaisie.replace(',', '.'));
    if (isNaN(n) || n <= 0) return;
    setSauvegardeEnCours(true);
    await onSaisirMontant(document, n);
    setSauvegardeEnCours(false);
    setModeEdition(false);
    setValeurSaisie('');
  };

  // Pour les régularisations : détecter trop-perçu ou complément depuis le résumé SENTINELLE
  // Normalisation NFD pour gérer les accents (créditeur, débiteur...)
  const normaliserTexte = (s: string) =>
    s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const texteDoc = document.resume || (document as any).description || '';
  const resumeNorm = normaliserTexte(texteDoc);
  const estTropPercu = estRegularisation && (
    resumeNorm.includes('crediteur') || resumeNorm.includes('trop-percu') ||
    resumeNorm.includes('trop percu') || resumeNorm.includes('remboursement') ||
    resumeNorm.includes('credit attendu') || resumeNorm.includes('solde crediteur')
  );
  const estComplement = estRegularisation && !estTropPercu && (
    resumeNorm.includes('debiteur') || resumeNorm.includes('complement') ||
    resumeNorm.includes('supplement') || resumeNorm.includes('reste a payer')
  );

  // donneesCles : réservées à SENTINELLE — aucun badge affiché à l'utilisateur

  return (
    <div className={`bg-white rounded-xl border p-4 transition-all ${
      estTropPercu ? 'border-emerald-200 bg-emerald-50/30'
      : regle ? 'border-gray-100 opacity-70'
      : 'border-gray-200 hover:shadow-sm'
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${typeCouleur}`}>{typeLabel}</span>
            {periode && <span className="text-xs text-gray-500 font-medium">{periode}</span>}
            {estTropPercu && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                <TrendingDown className="w-3 h-3" /> Trop-perçu — crédit attendu
              </span>
            )}
            {estComplement && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                <TrendingUp className="w-3 h-3" /> Complément dû
              </span>
            )}
            {regle && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                <CheckCircle className="w-3 h-3" /> {estRegularisation ? 'Crédit déduit' : 'Réglé'}
              </span>
            )}
            {regle && (() => {
              const dateAff = document.donneesCles?.['dateReglement'] || document.donneesCles?.['datePaiement'] || '';
              const moyenAff = document.donneesCles?.['moyenPaiement'] || document.donneesCles?.['modePaiement'] || '';
              const LABELS: Record<string, string> = {
                virement: 'Virement', prelevement: 'Prélèvement', cheque: 'Chèque',
                especes: 'Espèces', compensation: 'Déduit sur appel',
              };
              if (!dateAff && !moyenAff) return null;
              return (
                <span className="text-xs text-gray-400">
                  {dateAff && dateAff.split('-').reverse().join('/')}
                  {moyenAff && <> · {LABELS[moyenAff] || moyenAff}</>}
                </span>
              );
            })()}
          </div>

          <p className="text-sm font-semibold text-gray-800 truncate">{document.titre}</p>

          {/* Montant — éditable par l'humain */}
          {modeEdition ? (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="number"
                value={valeurSaisie}
                onChange={e => setValeurSaisie(e.target.value)}
                placeholder="0,00"
                min="0"
                step="0.01"
                autoFocus
                className="w-32 px-2 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
              <span className="text-sm text-gray-500">€</span>
              <button
                onClick={handleConfirmerMontant}
                disabled={sauvegardeEnCours || !valeurSaisie}
                className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {sauvegardeEnCours ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                OK
              </button>
              <button
                onClick={() => { setModeEdition(false); setValeurSaisie(''); }}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Annuler
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              {montant !== null ? (
                <p className={`text-xl font-bold ${estTropPercu ? 'text-emerald-700' : 'text-gray-900'}`}>
                  {fmt(montant)}
                </p>
              ) : (
                <p className="text-sm text-gray-400 italic">Montant non détecté</p>
              )}
              <button
                onClick={() => { setModeEdition(true); setValeurSaisie(montant !== null ? String(montant) : ''); }}
                className="text-gray-300 hover:text-blue-500 transition-colors"
                title="Saisir le montant manuellement"
              >
                <Receipt className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Résumé SENTINELLE — affiché uniquement sur les régularisations */}
          {estRegularisation && texteDoc && (
            <p className="text-xs text-gray-600 mt-2 italic leading-relaxed">
              {texteDoc}
            </p>
          )}

          <p className="text-xs text-gray-400 mt-2">{fmtDate(document.dateDocument as any)}</p>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <button
            onClick={() => onOuvrir(document)}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            <ExternalLink className="w-3 h-3" /> Voir
          </button>
          {!regle && montant !== null && (
            <button
              onClick={() => setShowModalRegle(true)}
              disabled={marquageEnCours}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
            >
              {marquageEnCours ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              {estRegularisation ? 'Crédit déduit' : 'Marquer réglé'}
            </button>
          )}
          {regle && !document.donneesCles?.['modePaiement'] && !document.donneesCles?.['moyenPaiement'] && (
            <button
              onClick={() => setShowModalRegle(true)}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-emerald-600 transition-colors"
              title="Compléter les informations de règlement"
            >
              <Receipt className="w-3 h-3" /> Compléter
            </button>
          )}

          {/* Modale règlement — portal pour éviter le fond transparent */}
          {showModalRegle && createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModalRegle(false)}>
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 mx-4" onClick={e => e.stopPropagation()}>
                <h2 className="text-base font-bold text-gray-900 mb-4">
                  {estRegularisation ? 'Confirmer le crédit' : 'Confirmer le règlement'}
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {estRegularisation ? 'Mode de déduction' : 'Mode de règlement'}
                    </label>
                    <select
                      value={moyenPaiement}
                      onChange={e => setMoyenPaiement(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-400 focus:outline-none"
                    >
                      {MOYENS_PAIEMENT.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={dateReglement}
                      onChange={e => setDateReglement(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowModalRegle(false)}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirmerReglement}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Confirmer
                  </button>
                </div>
              </div>
            </div>,
            window.document.body
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function MaComptabilitePage() {
  const { firebaseUser, userProfile } = useAuth();
  const { coproprieteId } = useCopropriete();
  const navigate = useNavigate();

  const estBailleur = userProfile?.vigieInfo?.estBailleur === true;
  const [onglet, setOnglet] = useState<'charges' | 'location'>('charges');
  const [anneeSelectionnee, setAnneeSelectionnee] = useState<number>(new Date().getFullYear());

  // ---- Documents GED ----
  const [docsComptables, setDocsComptables] = useState<Document[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [marquageEnCours, setMarquageEnCours] = useState<string | null>(null);

  // ---- Locataires ----
  const [locataires, setLocataires] = useState<Locataire[]>([]);
  const [totalLoyersGED, setTotalLoyersGED] = useState(0);
  const [loadingLoc, setLoadingLoc] = useState(false);

  // ---- Modales ----
  const [showModalLocataire, setShowModalLocataire] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- Formulaires ----
  const [formLoc, setFormLoc] = useState<Partial<CreateLocataireData>>({ loyerMensuel: 0, chargesProvision: 0 });

  // ============================================
  // CHARGEMENT
  // ============================================

  const chargerDocs = useCallback(async () => {
    if (!coproprieteId) return;
    setLoadingDocs(true);
    try {
      const docs = await getDocuments(coproprieteId, { categorie: 'comptabilite' });
      setDocsComptables(docs.filter(d => !d.estArchive));
    } catch (e) {
      console.error('[MaComptabilite]', e);
    } finally {
      setLoadingDocs(false);
    }
  }, [coproprieteId]);

  const chargerLocataires = useCallback(async (annee?: number) => {
    if (!firebaseUser || !coproprieteId) return;
    setLoadingLoc(true);
    const anneeFiltre = annee ?? anneeSelectionnee;
    try {
      const data = await getLocataires(firebaseUser.uid);
      setLocataires(data);
      // Calcul loyers depuis les quittances GED filtrées par année
      const docsGED = await getDocuments(coproprieteId, { categorie: 'mon_bien' });
      const total = docsGED
        .filter(d => {
          if ((d.type as string) !== 'quittance_loyer' || d.estArchive) return false;
          // Filtrer par moisConcerne (format YYYY-MM) ou dateDocument
          const mois = (d as any).moisConcerne as string | undefined;
          if (mois) return mois.startsWith(String(anneeFiltre));
          const dateDoc = (d.dateDocument as any)?.toDate?.();
          if (dateDoc) return (dateDoc as Date).getFullYear() === anneeFiltre;
          return false;
        })
        .reduce((s, d) => {
          const t = (d as any).total;
          if (typeof t === 'number') return s + t;
          const lhc = (d as any).loyerHC ?? 0;
          const ch = (d as any).charges ?? 0;
          return s + lhc + ch;
        }, 0);
      setTotalLoyersGED(total);
    } catch (e) {
      console.error('[MaComptabilite]', e);
    } finally {
      setLoadingLoc(false);
    }
  }, [firebaseUser, coproprieteId, anneeSelectionnee]);

  useEffect(() => {
    chargerDocs();
    if (estBailleur) chargerLocataires();
  }, [chargerDocs, chargerLocataires, estBailleur]);

  // Recharger les loyers quand l'année change
  useEffect(() => {
    if (estBailleur) chargerLocataires(anneeSelectionnee);
  }, [anneeSelectionnee]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // MARQUER RÉGLÉ — écrit dans donneesCles du document GED
  // ============================================

  const marquerRegle = async (document: any) => {
    setMarquageEnCours(document.id);
    const moyenPaiement: string = document._moyenPaiement || '';
    const dateReglement: string = document._dateReglement || '';
    try {
      await updateDocument(document.id, {
        donneesCles: {
          ...document.donneesCles,
          statutPaiement: 'oui',
          ...(moyenPaiement ? { modePaiement: moyenPaiement, moyenPaiement } : {}),
          ...(dateReglement ? { datePaiement: dateReglement, dateReglement } : {}),
        },
      });
      await chargerDocs();
    } catch (e) {
      console.error(e);
    } finally {
      setMarquageEnCours(null);
    }
  };

  const saisirMontant = async (document: Document, montant: number) => {
    try {
      await updateDocument(document.id, {
        donneesCles: { ...document.donneesCles, montant: String(montant) },
      });
      await chargerDocs();
    } catch (e) {
      console.error(e);
    }
  };

  // ============================================
  // LOCATAIRES
  // ============================================

  const handleCreerLocataire = async () => {
    if (!firebaseUser) return;
    if (!formLoc.nom?.trim() || !formLoc.prenom?.trim() || !formLoc.adresseBien?.trim() || !formLoc.dateEntree) {
      setError("Nom, prénom, adresse et date d'entrée obligatoires"); return;
    }
    setSaving(true); setError(null);
    try {
      await createLocataire(firebaseUser.uid, formLoc as CreateLocataireData);
      setShowModalLocataire(false);
      setFormLoc({ loyerMensuel: 0, chargesProvision: 0 });
      await chargerLocataires();
    } catch { setError('Impossible de créer le locataire'); }
    finally { setSaving(false); }
  };

  // ============================================
  // CALCULS SYNTHÈSE
  // ============================================

  // Régularisations exclues du total "en attente" — leur nature (débit/crédit) est indéterminée
  // et leur montant extrait par SENTINELLE peut être non fiable. Elles restent visibles dans leur groupe.
  const docsEnAttente = docsComptables.filter(d =>
    !estDocumentRegle(d) &&
    extraireMontant(d) !== null &&
    !TYPES_REGULARISATION.includes(d.type)
  );
  const totalBrutEnAttente = docsEnAttente.reduce((s, d) => s + (extraireMontant(d) || 0), 0);

  // Crédits trop-perçu : régularisations non réglées avec solde créditeur
  // Déduits du total dû — ce qui est dû au copropriétaire réduit ce qu'il doit au syndic
  const totalCreditsRegul = docsComptables
    .filter(d => TYPES_REGULARISATION.includes(d.type) && !estDocumentRegle(d))
    .reduce((s, d) => {
      const texte = d.resume || (d as any).description || '';
      const texteNorm = texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
      const estCredit = texteNorm.includes('crediteur') || texteNorm.includes('trop-percu') ||
        texteNorm.includes('trop percu') || texteNorm.includes('remboursement');
      return estCredit ? s + (extraireMontant(d) || 0) : s;
    }, 0);

  const totalEnAttente = Math.max(0, totalBrutEnAttente - totalCreditsRegul);
  const totalLoyersPercus = totalLoyersGED;
  const nbImpayes = 0; // Les impayés sont gérés dans la fiche locataire (appels sans quittance)

  const statutGlobal: 'ok' | 'due' | 'alert' =
    nbImpayes > 0 ? 'alert' : docsEnAttente.length > 0 ? 'due' : 'ok';

  const bandeauLabel =
    statutGlobal === 'ok' ? 'Situation à jour — aucun document en attente'
    : statutGlobal === 'due' ? `${docsEnAttente.length} document${docsEnAttente.length > 1 ? 's' : ''} en attente de règlement`
    : `${nbImpayes} paiement${nbImpayes > 1 ? 's' : ''} en retard ou impayé${nbImpayes > 1 ? 's' : ''}`;

  const bandeauDetail =
    statutGlobal === 'due'
      ? totalCreditsRegul > 0
        ? `${fmt(totalBrutEnAttente)} — ${fmt(totalCreditsRegul)} crédit trop-perçu = ${fmt(totalEnAttente)} net à régler`
        : `${fmt(totalEnAttente)} restent à régler`
      : undefined;

  // Groupement
  const docsCharges = docsComptables.filter(d => TYPES_CHARGES_COPRO.includes(d.type));
  const docsReguls = docsComptables.filter(d => TYPES_REGULARISATION.includes(d.type));
  const docsAutres = docsComptables.filter(d => !TYPES_CHARGES_COPRO.includes(d.type) && !TYPES_REGULARISATION.includes(d.type));

  // ============================================
  // TAB CHARGES COPRO
  // ============================================

  const renderTabCharges = () => {
    if (loadingDocs) return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-500 text-sm">Chargement...</span>
      </div>
    );

    return (
      <div className="space-y-5">
        {docsComptables.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-xl border border-gray-200">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Receipt className="w-6 h-6 text-blue-600" />
            </div>
            <p className="font-semibold text-gray-700 mb-1">Aucun document comptable</p>
            <p className="text-sm text-gray-400 max-w-xs mx-auto">
              Déposez vos appels de fonds, budgets et relevés — SENTINELLE s'occupe du reste.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {[
              { titre: 'Appels de fonds & charges', docs: docsCharges },
              { titre: 'Régularisations', docs: docsReguls },
              { titre: 'Autres documents', docs: docsAutres },
            ].filter(g => g.docs.length > 0).map(({ titre, docs }) => (
              <div key={titre}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{titre}</h3>
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{docs.length}</span>
                </div>
                <div className="space-y-2">
                  {docs.map(d => (
                    <CarteDocument
                      key={d.id}
                      document={d}
                      onMarquerRegle={marquerRegle}
                      onOuvrir={doc => navigate(`/vigie/documents/${doc.id}`)}
                      onSaisirMontant={saisirMontant}
                      marquageEnCours={marquageEnCours === d.id}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // TAB MA LOCATION
  // ============================================

  const renderTabLocation = () => {
    if (loadingLoc) return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        <span className="ml-3 text-gray-500 text-sm">Chargement...</span>
      </div>
    );

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {locataires.length === 0 ? 'Aucun locataire' : `${locataires.length} locataire${locataires.length > 1 ? 's' : ''}`}
          </p>
          <button
            onClick={() => { setError(null); setShowModalLocataire(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter un locataire
          </button>
        </div>

        {locataires.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-xl border border-dashed border-amber-200">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Home className="w-6 h-6 text-amber-600" />
            </div>
            <p className="font-semibold text-gray-700 mb-1">Aucun locataire enregistré</p>
            <p className="text-sm text-gray-400 max-w-xs mx-auto mb-4">
              Ajoutez votre locataire pour suivre les loyers, générer les quittances et calculer les charges récupérables.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {locataires.map(loc => {
              return (
                <button
                  key={loc.id}
                  onClick={() => navigate(`/vigie/locataires/${loc.id}`)}
                  className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 hover:shadow-sm transition-all text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-sm shrink-0">
                      {loc.prenom?.charAt(0)?.toUpperCase() || 'L'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{loc.prenom} {loc.nom}</p>
                      <p className="text-xs text-gray-500">{loc.adresseBien || 'Adresse non renseignée'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-bold text-gray-800">{fmt(loc.loyerMensuel || 0)}/mois</p>
                      <p className="text-xs text-gray-400">Voir la fiche</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // MODALES
  // ============================================

  const renderModalLocataire = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Ajouter un locataire</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(['prenom', 'nom'] as const).map(k => (
            <div key={k}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{k === 'prenom' ? 'Prénom *' : 'Nom *'}</label>
              <input type="text" value={(formLoc as any)[k] || ''}
                onChange={e => setFormLoc(f => ({ ...f, [k]: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Adresse du bien loué *</label>
            <input type="text" value={formLoc.adresseBien || ''}
              onChange={e => setFormLoc(f => ({ ...f, adresseBien: e.target.value }))}
              placeholder="Ex : Apt 12, 24 rue des Cèdres"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={formLoc.email || ''}
              onChange={e => setFormLoc(f => ({ ...f, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <input type="tel" value={formLoc.telephone || ''}
              onChange={e => setFormLoc(f => ({ ...f, telephone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date d'entrée *</label>
            <input type="date"
              value={formLoc.dateEntree instanceof Date ? formLoc.dateEntree.toISOString().split('T')[0] : ''}
              onChange={e => { const d = e.target.value ? new Date(e.target.value) : undefined; setFormLoc(f => ({ ...f, dateEntree: d })); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
          </div>
          {[
            { label: 'Loyer mensuel (€)', key: 'loyerMensuel' },
            { label: 'Charges provision (€/mois)', key: 'chargesProvision' },
            { label: 'Dépôt de garantie (€)', key: 'depotGarantie' },
          ].map(({ label, key }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              <input type="number" value={(formLoc as any)[key] || 0}
                onChange={e => setFormLoc(f => ({ ...f, [key]: parseFloat(e.target.value) || 0 }))}
                min="0" step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
            </div>
          ))}
        </div>
        {error && <p className="mt-3 text-sm text-red-600 bg-red-50 p-2 rounded-lg">{error}</p>}
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={() => { setShowModalLocataire(false); setError(null); }}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
          <button onClick={handleCreerLocataire} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Créer le locataire
          </button>
        </div>
      </div>
    </div>
  );


  // ============================================
  // RENDU PRINCIPAL
  // ============================================

  return (
    <div className="space-y-4">

      {/* En-tête */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-blue-900 rounded-xl">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Ma comptabilité</h1>
            <p className="text-sm text-gray-500">
              {estBailleur ? 'Charges copropriété & suivi locatif' : 'Suivi de vos charges de copropriété'}
            </p>
          </div>
        </div>
        {estBailleur && (
          <span className="flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
            <Home className="w-3.5 h-3.5" /> Bailleur
          </span>
        )}
      </div>

      {/* Bandeau situation */}
      <BandeauSituation statut={statutGlobal} label={bandeauLabel} detail={bandeauDetail} />

      {/* Encart dépôt document — aligné sur DocumentsVigiePage */}
      <button
        onClick={() => navigate('/vigie/documents/ajouter', {
          state: { categorieDefaut: 'comptabilite', retourVers: '/vigie/comptabilite' }
        })}
        className="w-full flex items-center gap-4 px-5 py-4 bg-amber-50 border-2 border-dashed border-amber-300 rounded-xl hover:bg-amber-100 hover:border-amber-400 transition-colors text-left"
      >
        <div className="relative w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shrink-0">
          <Upload className="w-5 h-5 text-white" />
          <span className="absolute inset-0 rounded-xl bg-amber-400 animate-ping opacity-40" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">Déposer un document comptable</p>
          <p className="text-sm text-gray-500">SENTINELLE extrait les montants, la période et détecte les anomalies automatiquement</p>
        </div>
        <svg className="w-5 h-5 text-amber-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Synthèse chiffrée bailleur */}
      {estBailleur && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-red-600" />
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">En attente</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{fmt(totalEnAttente)}</p>
            <p className="text-xs text-gray-400 mt-1">Charges à régler</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Loyers</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{fmt(totalLoyersPercus)}</p>
            <div className="flex items-center gap-2 mt-1">
              <button
                onClick={() => setAnneeSelectionnee(a => a - 1)}
                className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                title="Année précédente"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-500 font-semibold min-w-[2.5rem] text-center">{anneeSelectionnee}</span>
              <button
                onClick={() => setAnneeSelectionnee(a => Math.min(a + 1, new Date().getFullYear()))}
                className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                disabled={anneeSelectionnee >= new Date().getFullYear()}
                title="Année suivante"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${totalLoyersPercus - totalEnAttente >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${totalLoyersPercus - totalEnAttente >= 0 ? 'bg-blue-100' : 'bg-amber-100'}`}>
                <Wallet className={`w-4 h-4 ${totalLoyersPercus - totalEnAttente >= 0 ? 'text-blue-600' : 'text-amber-600'}`} />
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Solde net</span>
            </div>
            <p className={`text-xl font-bold ${totalLoyersPercus - totalEnAttente >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
              {fmt(totalLoyersPercus - totalEnAttente)}
            </p>
            <p className="text-xs text-gray-400 mt-1">Loyers - charges</p>
          </div>
        </div>
      )}

      {/* Tabs bailleur / contenu direct occupant */}
      {estBailleur ? (
        <div className="space-y-4">
          {/* Cartes-boutons navigation */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setOnglet('charges')}
              className={`relative flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all text-left ${
                onglet === 'charges'
                  ? 'bg-blue-900 border-blue-900 text-white shadow-lg'
                  : 'bg-white border-blue-200 text-blue-900 hover:border-blue-400 hover:shadow-md'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${onglet === 'charges' ? 'bg-white/20' : 'bg-blue-100'}`}>
                <Building2 className={`w-5 h-5 ${onglet === 'charges' ? 'text-white' : 'text-blue-900'}`} />
              </div>
              <div className="text-center">
                <p className={`text-sm font-bold ${onglet === 'charges' ? 'text-white' : 'text-blue-900'}`}>Mes charges</p>
                <p className={`text-xs mt-0.5 ${onglet === 'charges' ? 'text-blue-200' : 'text-gray-400'}`}>Appels de fonds, règlements</p>
              </div>
              {docsEnAttente.length > 0 && (
                <span className="absolute top-2 right-2 w-5 h-5 bg-amber-500 text-white rounded-full text-xs flex items-center justify-center font-bold">
                  {docsEnAttente.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setOnglet('location')}
              className={`relative flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all text-left ${
                onglet === 'location'
                  ? 'bg-amber-500 border-amber-500 text-white shadow-lg'
                  : 'bg-white border-amber-200 text-amber-700 hover:border-amber-400 hover:shadow-md'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${onglet === 'location' ? 'bg-white/20' : 'bg-amber-100'}`}>
                <Home className={`w-5 h-5 ${onglet === 'location' ? 'text-white' : 'text-amber-600'}`} />
              </div>
              <div className="text-center">
                <p className={`text-sm font-bold ${onglet === 'location' ? 'text-white' : 'text-amber-700'}`}>Mon locataire</p>
                <p className={`text-xs mt-0.5 ${onglet === 'location' ? 'text-amber-100' : 'text-gray-400'}`}>Quittances, suivi loyer</p>
              </div>
              {nbImpayes > 0 && (
                <span className="absolute top-2 right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center font-bold">
                  {nbImpayes}
                </span>
              )}
            </button>
          </div>
          {onglet === 'charges' ? renderTabCharges() : renderTabLocation()}
        </div>
      ) : (
        renderTabCharges()
      )}

      {/* Incitation bailleur */}
      {!estBailleur && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
          <Home className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span>Vous louez votre appartement ?</span>
          <button
            onClick={() => navigate('/vigie/profil')}
            className="flex items-center gap-1 text-blue-600 font-medium hover:underline flex-shrink-0"
          >
            Activez le profil bailleur <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {showModalLocataire && renderModalLocataire()}
    </div>
  );
}
